'use client';

import { useMemo, useCallback } from 'react';
import { useUpdateURL } from '../utils/product-context-helpers';
import {
  buildBundleState,
  getBundleProductFromSearchParams,
  withFilteredVariants
} from '../utils/bundle-state';
import { useProduct } from './use-product';
import { useSelectionStore } from './private/use-selection-store';
import { useResolvedOptions } from './private/use-resolved-options';

/**
 * Bundle specific configuration state, which has the selected bundle product
 * + its setter, the offers (the variants in the bundle), and the price summary.
 *
 * Requires <Suspense>.
 */
export function useBundleSelection() {
  const { isBundlePage, bundleData, productData, selectorProducts } =
    useProduct();
  const selectionStore = useSelectionStore();

  const { searchParams, selectedOptions } = useResolvedOptions();
  const updateURL = useUpdateURL();

  const { selectedBundleProduct, setSelectedBundleProduct } = selectionStore;

  // `withFilteredVariants` inside here builds a new object, so this has to be
  // memoized
  const bundleProductFromURL = useMemo(
    () => getBundleProductFromSearchParams(selectorProducts, searchParams),
    [selectorProducts, searchParams]
  );

  const setSelectedProduct = useCallback(
    (selectedProduct, secondaryAttribute) => {
      setSelectedBundleProduct(
        withFilteredVariants(selectedProduct, secondaryAttribute)
      );

      // Update the URL with the selected product's handle for upsell toggles
      updateURL({ selectedProduct: selectedProduct.shopifyProduct.handle });
    },
    [setSelectedBundleProduct, updateURL]
  );

  const selectedProduct = selectedBundleProduct ?? bundleProductFromURL;

  return useMemo(
    () =>
      buildBundleState({
        isBundlePage,
        bundleData,
        shopifyProduct: productData.shopifyProduct,
        options: selectedOptions,
        selectedProduct,
        setSelectedProduct
      }),
    [
      isBundlePage,
      bundleData,
      productData.shopifyProduct,
      selectedOptions,
      selectedProduct,
      setSelectedProduct
    ]
  );
}
